import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HighlightDrawer from '../components/HighlightDrawer';

const HIGHLIGHTS = [
    {
        id: 'rnd-leadership',
        title: 'R&D Project Leadership',
        icon: 'strategy',
        description: 'Leading EU- and nationally funded R&D projects from proposal development to validated technical delivery.',
        detailedDescription: 'I have extensive experience managing applied research and innovation projects in both academic and industrial contexts. My role typically spans proposal preparation, consortium building, technical coordination, and delivery of measurable outcomes aligned with funding, industrial, and research constraints.',
        bullets: [
            'Horizon Europe, KDT JU, ECSEL JU, TÜBİTAK projects',
            'Proposal writing & consortium building',
            'Work package leadership and KPI tracking',
            'Deliverables, milestones, and technical reporting',
            'Academia–industry coordination'
        ]
    },
    {
        id: 'technical-lead',
        title: 'Technical Project Lead & System Architecture',
        icon: 'architecture',
        description: 'Technical leadership and system-level design of complex IoT, edge, and AI-enabled platforms.',
        detailedDescription: 'I operate at the system level, translating requirements into scalable architectures while guiding technical teams through implementation. I balance performance, reliability, and deployment constraints, ensuring that complex systems are not only designed correctly but also delivered and validated in real-world environments.',
        bullets: [
            'System architecture design (edge–cloud)',
            'Technical leadership of multidisciplinary teams',
            'IoT, networking, and distributed systems',
            'End-to-end system integration',
            'Performance, scalability, and reliability design'
        ]
    },
    {
        id: 'edge-ai-data',
        title: 'Edge AI & Data-Driven Systems',
        icon: 'insights',
        description: 'Designing data-driven and AI-enabled systems for real-time perception, analytics, and decision support.',
        detailedDescription: 'My expertise covers the full lifecycle of data-driven systems, from real-time data acquisition and streaming to edge AI inference and analytics-driven decision support. I focus on deployable, efficient, and scalable solutions suitable for industrial and resource-constrained environments.',
        bullets: [
            'Edge AI model deployment and optimization',
            'Multimodal perception (thermal, RGB, sensor data)',
            'Real-time data pipelines (Kafka, MQTT)',
            'Databases and storage (PostgreSQL, MongoDB)',
            'Industrial analytics and decision support systems'
        ]
    }
];


function Home() {

    const [selectedHighlight, setSelectedHighlight] = useState(null);

    return (
        <div className="relative flex h-auto min-h-screen w-full flex-col group/design-root overflow-x-hidden">
            <HighlightDrawer
                isOpen={!!selectedHighlight}
                onClose={() => setSelectedHighlight(null)}
                highlight={selectedHighlight}
            />
            <div className="layout-container flex h-full grow flex-col">
                <Header />
                <main className="flex-1">
                    {/* Hero Section */}
                    <div className="max-w-[1200px] mx-auto px-4 lg:px-10 py-12 @container">

                        <div className="flex flex-col gap-6 py-10 @[864px]:flex-row @[864px]:items-center">
                            <div
                                className="w-full bg-center bg-no-repeat aspect-square max-w-[480px] bg-cover rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800"
                                data-alt="Professional portrait of a computer engineer in tech setting"
                                style={{ backgroundImage: 'url("/assets/profile.png")' }}
                            >
                            </div>
                            <div className="flex flex-col gap-6 @[480px]:gap-8 @[864px]:pl-10">
                                <div className="flex flex-col gap-4 text-left">
                                    <h1 className="text-white md:text-[#284A58] text-4xl font-black leading-tight tracking-[-0.033em] @[480px]:text-6xl font-display">
                                        Yavuz Selim Bostancı
                                    </h1>
                                    <p className="text-white md:text-[#284A58] font-bold tracking-widest uppercase text-sm">Computer Engineer &amp; R&amp;D Technical Lead</p>
                                    <h2 className="text-slate-600 dark:text-slate-400 text-base font-normal leading-relaxed @[480px]:text-xl max-w-xl">
                                        Computer Engineer with 7.5+ years of experience across academic and industrial R&amp;D environments. Strong background in IoT systems, networking, edge/AI-enabled systems, and data-driven architectures.
                                    </h2>
                                </div>
                                <div className="flex-wrap gap-4 flex">
                                    <Link
                                        to="/experience"
                                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        className="flex min-w-[140px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 bg-[#284A58] text-white text-base font-bold leading-normal tracking-[0.015em] hover:shadow-lg hover:shadow-[#284A58]/30 transition-all"
                                    >
                                        <span className="truncate">View Projects</span>
                                    </Link>
                                    <Link
                                        to="/contact"
                                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        className="flex min-w-[140px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-6 bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-white text-base font-bold leading-normal tracking-[0.015em] hover:bg-slate-300 dark:hover:bg-slate-700 transition-all"
                                    >
                                        <span className="truncate">Contact Me</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Technical Highlights Section */}
                    <section className="max-w-[1200px] mx-auto px-4 lg:px-10 pb-20">
                        <div className="flex items-center gap-4 mb-8">
                            <h2 className="text-gray-900 dark:text-gray-400 text-[28px] font-bold leading-tight tracking-[-0.015em] font-display">Technical Highlights</h2>
                            <div className="h-px grow bg-slate-200 dark:bg-slate-800"></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {HIGHLIGHTS.map((highlight) => (
                                <div
                                    key={highlight.id}
                                    onClick={() => setSelectedHighlight(highlight)}
                                    className="flex flex-1 gap-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex-col hover:border-primary transition-all group cursor-pointer hover:shadow-lg hover:translate-y-[-4px]"
                                >
                                    <div className="text-primary dark:text-sky-300 bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                                        <span className="material-symbols-outlined text-3xl">{highlight.icon}</span>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <h3 className="text-slate-900 dark:text-white text-xl font-bold leading-tight">{highlight.title}</h3>
                                        <p className="text-slate-500 dark:text-slate-400 text-base font-normal leading-relaxed">
                                            {highlight.description}
                                        </p>
                                    </div>
                                    <div className="mt-auto pt-4 flex items-center text-primary dark:text-sky-300 text-sm font-bold">
                                        <span>View Details</span>
                                        <span className="material-symbols-outlined text-lg ml-1">arrow_forward</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    {/* Recent Experience Section */}
                    <section className="bg-slate-50 dark:bg-slate-900/50 py-20 border-t border-slate-200 dark:border-slate-800">
                        <div className="max-w-[1200px] mx-auto px-4 lg:px-10">
                            <div className="flex items-center gap-4 mb-10">
                                <h2 className="text-gray-900 dark:text-gray-400 text-[28px] font-bold leading-tight tracking-[-0.015em] font-display">Recent Experience</h2>
                                <div className="h-px grow bg-slate-200 dark:border-slate-800"></div>
                            </div>
                            <div className="space-y-8">
                                <div className="flex flex-col md:flex-row gap-4 md:gap-10 p-6 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                                    <div className="min-w-[120px] text-primary dark:text-sky-300 font-bold text-sm uppercase tracking-wider">2022 — Present</div>
                                    <div className="flex-1">
                                        <h3 className="text-slate-900 dark:text-white text-xl font-bold">Technical Lead &amp; Project Manager</h3>
                                        <p className="text-slate-500 dark:text-slate-400 font-medium mb-4">
                                            <a href="https://bigtri.net" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">BigTRI Bilişim A.Ş.</a>
                                        </p>
                                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                            I serve as a technical lead in multiple EU and national R&D projects, leading a seven-person multidisciplinary team and taking end-to-end responsibility for proposal preparation, consortium building, project execution, reporting, and delivery, while providing leadership across IoT, networking, image processing, edge AI, and data analytics, with hands-on experience spanning pre-award, ongoing, and completed phases of R&amp;D projects.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row gap-4 md:gap-10 p-6 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                                    <div className="min-w-[120px] text-primary dark:text-sky-300 font-bold text-sm uppercase tracking-wider">2020 — 2022</div>
                                    <div className="flex-1">
                                        <h3 className="text-slate-900 dark:text-white text-xl font-bold">Lead Research Engineer</h3>
                                        <p className="text-slate-500 dark:text-slate-400 font-medium mb-4">
                                            <a href="https://venit.org" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">VeNIT Lab (Marmara University)</a>
                                        </p>
                                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                            I provide technical leadership and system-level development in national and EU-funded R&amp;D projects, leading the end-to-end design of IoT and networking-based monitoring systems from data collection to backend services, developing network performance measurement tools and data-driven architectures, and working closely with industrial partners while actively contributing to project reporting and formal deliverables.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-col md:flex-row gap-4 md:gap-10 p-6 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                                    <div className="min-w-[120px] text-primary dark:text-sky-300 font-bold text-sm uppercase tracking-wider">2018 — 2020</div>
                                    <div className="flex-1">
                                        <h3 className="text-slate-900 dark:text-white text-xl font-bold">Research Engineer</h3>
                                        <p className="text-slate-500 dark:text-slate-400 font-medium mb-4">
                                            <a href="https://venit.org" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">VeNIT Lab (Marmara University)</a>
                                        </p>
                                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                            I contributed to a TÜBİTAK 1505 University–Industry Collaboration Project titled WATMON (Wi-Fi Access and Traffic Monitoring), where I focused on network connectivity challenges, service emulation, network performance measurement, and the development of application-level services and graphical user interfaces.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-12 text-center">
                                <button
                                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                    className="text-[#284A58] dark:text-[#284A58] font-bold flex items-center gap-2 mx-auto hover:underline cursor-pointer"
                                >
                                    Go to Top
                                    <span className="material-symbols-outlined">arrow_upward</span>
                                </button>
                            </div>
                        </div>
                    </section>
                </main>
                {/* Footer */}
                <Footer />
            </div>
        </div>
    );
}

export default Home;

